/**
 * Pure offer-masonry geometry: fixed column width from container (not card count),
 * fewer cards stay that width and are centered; integer px to avoid overlap.
 */

export interface OfferMasonryMetrics {
    maxCols: number;
    packCols: number;
    colWidth: number;
    gap: number;
    /** Horizontal shift so a sparse pack is centered in the maxCols track. */
    offsetX: number;
}

export interface OfferMasonryPlacement {
    col: number;
    x: number;
    y: number;
}

export function computeOfferMasonryMetrics(
    width: number,
    gap: number,
    minCol: number,
    cardCount: number,
): OfferMasonryMetrics {
    const w = Math.max(0, Math.floor(width));
    const g = Math.max(0, gap);
    const min = Math.max(1, minCol);
    const maxCols = Math.max(1, Math.floor((w + g) / (min + g)));
    const colWidth = Math.max(1, Math.floor((w - g * (maxCols - 1)) / maxCols));
    const trackWidth = maxCols * colWidth + (maxCols - 1) * g;
    const trackPad = Math.max(0, Math.floor((w - trackWidth) / 2));
    const packCols = Math.min(maxCols, Math.max(1, cardCount));
    const sparseShift = Math.round(((maxCols - packCols) * (colWidth + g)) / 2);
    return {
        maxCols,
        packCols,
        colWidth,
        gap: g,
        offsetX: trackPad + sparseShift,
    };
}

/**
 * Shortest-column pack. cardHeights must be ceil'd integers.
 * No last-row horizontal centering (would slide cards under taller neighbors).
 */
export function placeOfferMasonryCards(
    cardHeights: number[],
    metrics: OfferMasonryMetrics,
): { placements: OfferMasonryPlacement[]; containerHeight: number } {
    const { packCols, colWidth, gap, offsetX } = metrics;
    const colHeights = new Array(packCols).fill(0) as number[];
    const placements: OfferMasonryPlacement[] = [];

    for (let i = 0; i < cardHeights.length; i++) {
        let col = 0;
        for (let c = 1; c < packCols; c++) {
            if (colHeights[c] < colHeights[col]) col = c;
        }
        const h = Math.max(1, Math.ceil(cardHeights[i] || 0));
        const x = offsetX + col * (colWidth + gap);
        const y = colHeights[col];
        placements.push({ col, x, y });
        colHeights[col] = y + h + gap;
    }

    const maxColHeight = colHeights.length ? Math.max.apply(null, colHeights) : 0;
    const containerHeight = Math.max(0, maxColHeight - gap);
    return { placements, containerHeight };
}
